import { useLayoutEffect, useRef, useState } from 'react';

export function useMeasuredTabs(items: readonly { key: string }[], activeKey: string | undefined) {
  const bar = useRef<HTMLDivElement>(null);
  const measures = useRef<HTMLDivElement>(null);
  const [visibleKeys, setVisibleKeys] = useState(items.map((item) => item.key));
  useLayoutEffect(() => {
    const container = bar.current;
    const track = measures.current;
    if (!container || !track) return;
    const measure = (): void => {
      const style = getComputedStyle(container);
      const width =
        container.clientWidth -
        parseFloat(style.paddingLeft || '0') -
        parseFloat(style.paddingRight || '0');
      if (width <= 0) return;
      const gap = parseFloat(style.columnGap || '0') || 0;
      const widths = Array.from(track.querySelectorAll<HTMLElement>('[data-measure-tab]')).map(
        (element) => element.getBoundingClientRect().width,
      );
      const moreWidths = Array.from(track.querySelectorAll<HTMLElement>('[data-measure-more]')).map(
        (element) => element.getBoundingClientRect().width,
      );
      let visible = [...items];
      if (
        widths.reduce((sum, value) => sum + value, 0) + gap * Math.max(0, items.length - 1) >
        width
      ) {
        const active = items.find((item) => item.key === activeKey);
        for (let count = items.length - 1; count >= 1; count--) {
          const candidates = items.slice(0, count);
          if (active && !candidates.includes(active)) candidates[count - 1] = active;
          visible = items.filter((item) => candidates.includes(item));
          const used =
            visible.reduce((sum, item) => sum + (widths[items.indexOf(item)] ?? 0), 0) +
            (moreWidths[items.length - count - 1] ?? 0) +
            gap * count;
          if (used <= width) break;
        }
      }
      const next = visible.map((item) => item.key);
      setVisibleKeys((previous) => (previous.join('\0') === next.join('\0') ? previous : next));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(container);
    observer?.observe(track);
    window.addEventListener('resize', measure);
    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) measure();
    });
    return () => {
      cancelled = true;
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [items, activeKey]);
  return { bar, measures, visibleKeys };
}
